import { useCallback, useEffect, useState } from "react";

/**
 * What is left of the dock's combobox coordination now that they are PrimeReact
 * Dropdowns.
 *
 * PrimeReact owns each list's open state and already closes it on outside click, on
 * scroll and on Escape, so none of that lives here any more. The one thing it cannot
 * know is that Escape has a second job in this dock: collapsing it. Without the
 * guard below, one Escape would close the open list *and* collapse the dock —
 * pressing a key should never do two things at once.
 *
 * Each Select reports itself through onListShow / onListHide.
 */
export function useDropdowns(onEscape: () => void) {
  const [openCount, setOpenCount] = useState(0);

  const onListShow = useCallback(() => setOpenCount((n) => n + 1), []);
  const onListHide = useCallback(() => setOpenCount((n) => Math.max(0, n - 1)), []);
  const anyListOpen = openCount > 0;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // the Dropdown is closing itself on this same keypress; leave the dock alone
      if (anyListOpen) return;
      onEscape();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [anyListOpen, onEscape]);

  // anyListOpen stays local: it exists only to arbitrate the Escape key above, and
  // no caller has ever needed to know whether a list is open.
  return { onListShow, onListHide };
}
